import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  tableCellClasses,
  Grid,
  Stack,
  Chip,
  styled,
} from "@mui/material";
import {
  Search,
  Filter,
  ChevronRight,
  Ticket as TicketIcon,
  Clock,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import CreateTicket from "./CreateTicket";
import { useNavigate } from "react-router-dom";
import TicketCards from "./Card";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

const GlassCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  borderRadius: '24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
  border: '1px solid #F0F0F5',
  padding: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
  },
}));

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  color: '#6B7280',
  fontWeight: 700,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  padding: '16px 24px',
  borderBottom: '1px solid #F3F4F6',
  backgroundColor: '#fff',
}));

const StyledBodyCell = styled(TableCell)(({ theme }) => ({
  padding: '20px 24px',
  fontSize: '14px',
  color: '#1F2937',
  borderBottom: '1px solid #F9FAFB',
}));

const StatusBadge = ({ status }) => {
  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return { bg: '#ECFDF5', color: '#059669', icon: <AlertCircle size={14} /> };
      case "in progress":
        return { bg: '#FFF7ED', color: '#D97706', icon: <Clock size={14} /> };
      case "closed":
        return { bg: '#FEF2F2', color: '#DC2626', icon: <CheckCircle2 size={14} /> };
      case "waiting on customer":
        return { bg: '#EFF6FF', color: '#2563EB', icon: <Clock size={14} /> };
      default:
        return { bg: '#F3F4FB', color: '#6366F1', icon: <TicketIcon size={14} /> };
    }
  };

  const styles = getStatusStyles(status);

  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.5,
      borderRadius: '20px',
      bgcolor: styles.bg,
      color: styles.color,
      fontWeight: 600,
      fontSize: '12px'
    }}>
      {styles.icon}
      {status}
    </Box>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return dateString || "N/A";
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const TicketTable = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    getAllTicket();
  }, []);

  useEffect(() => {
    const filtered = data.filter(ticket => 
      ticket.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.businessName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket._id?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchQuery, data]);

  const getAllTicket = async () => {
    try {
      const res = await axiosInstance.get(`${apiEndPointsConfig.ticketList}`);
      setStats(res.data.stats);
      setData(res?.data?.data || []);
      setFilteredData(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F8F9FC', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800} color="#111827">Agent Support Panel</Typography>
            <Typography variant="body2" color="#6B7280">Manage and respond to all customer tickets.</Typography>
          </Box>
          <CreateTicket
            getAllTicket={getAllTicket}
            open={open}
            setOpen={setOpen}
          />
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TicketCards stats={stats} />
      </Box>

      <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #F3F4F6' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ position: 'relative', width: '100%', flex: 1 }}>
              <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input
                type="text"
                placeholder="Search by user, company, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 48px',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  outline: 'none',
                  fontSize: '14px',
                  backgroundColor: '#F9FAFB'
                }}
              />
            </Box>
            <IconButton sx={{
              bgcolor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              width: { xs: '100%', sm: 'auto' },
              height: { xs: '48px', sm: 'auto' }
            }}>
              <Filter size={20} color="#6B7280" />
            </IconButton>
          </Stack>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <StyledHeaderCell>S.No</StyledHeaderCell>
                <StyledHeaderCell>User Name</StyledHeaderCell>
                <StyledHeaderCell>Company Name</StyledHeaderCell>
                <StyledHeaderCell>Assignee</StyledHeaderCell>
                <StyledHeaderCell>Mobile</StyledHeaderCell>
                <StyledHeaderCell>Status</StyledHeaderCell>
                <StyledHeaderCell>Ticket Type</StyledHeaderCell>
                <StyledHeaderCell>Create Date</StyledHeaderCell>
                <StyledHeaderCell align="right"></StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                    <Box sx={{ opacity: 0.5, mb: 2 }}>
                      <TicketIcon size={48} />
                    </Box>
                    <Typography variant="h6" color="textSecondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row, index) => (
                    <TableRow
                      key={row._id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' } }}
                      onClick={() => navigate(`/agent/tickets/${row._id}`)}
                    >
                      <StyledBodyCell sx={{ width: '60px' }}>
                        <Typography fontWeight={700} color="#4F46E5" sx={{ fontSize: '13px' }}>
                          {index + 1}
                        </Typography>
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '150px' }}>
                        <Typography fontWeight={600}>{row.name}</Typography>
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '180px' }}>{row.businessName}</StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '120px' }}>
                        <Chip 
                          label={row.assignee || 'Unassigned'} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '11px', fontWeight: 600 }}
                        />
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '130px' }}>{row.mobile}</StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '140px' }}>
                        <StatusBadge status={row.status} />
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '120px' }}>
                        <Chip
                          label={row.category}
                          size="small"
                          sx={{ bgcolor: '#F3F4F6', fontWeight: 600, fontSize: '11px', color: '#4B5563' }}
                        />
                      </StyledBodyCell>
                      <StyledBodyCell sx={{ minWidth: '130px' }}>{formatDate(row.createdAt)}</StyledBodyCell>
                      <StyledBodyCell align="right" sx={{ width: '50px' }}>
                        <IconButton size="small">
                          <ChevronRight size={20} color="#9CA3AF" />
                        </IconButton>
                      </StyledBodyCell>
                    </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </GlassCard>
    </Box>
  );
};

export default TicketTable;
